// A workaround for a defect in video.js, NOT support for anybody's transcoder.
//
// THE BUG. On an AES-128 encrypted HLS stream that uses fragmented MP4 (an
// `#EXT-X-MAP` init segment), video.js cannot decrypt the init segment,
// because it never gives its decryption worker an initialisation vector for
// it. Every rendition therefore fails on its very first file, video.js
// excludes that rendition and drops to the next, and when it runs out the
// stream is dead. Nothing plays, and autoplay has nothing to start.
//
// Concretely, in @videojs/http-streaming (bundled inside video.js):
//
//   media-segment-request.js  `decrypt({ …, key: segment.map.key })`, and
//                             `decrypt` sends `iv: key.iv` to the worker
//   segment-loader.js         the fallback that invents an IV when the
//                             playlist declares none runs ONLY for
//                             `segment.key`, never for `segment.map.key`
//   decrypter-worker.js       `new Uint32Array(data.iv.bytes, …)`
//                             → "Cannot read properties of undefined
//                                (reading 'bytes')"
//
// Confirmed present and IDENTICAL in VHS 3.17.0 (what video.js 8.22 bundles)
// through 3.17.5, the newest release at the time of writing — upgrading does
// not fix it.
//
// WHY THIS IS NOT A SLIPPERY SLOPE INTO FORMAT SUPPORT. This does not teach
// the player a new container, codec or delivery scheme, and it is not specific
// to any one host. AES-128 plus fMP4 is ordinary, spec-compliant HLS
// (RFC 8216 §4.3.2.4 says an EXT-X-KEY covers "every Media Initialization
// Section declared by an EXT-X-MAP tag" that follows it), and any transcoder
// producing it hits this. The player already claims to play HLS; this is the
// smallest possible correction to make that claim true.
//
// WHY A ZERO IV. The playlists that hit this declare no `IV` attribute on
// their `EXT-X-KEY`. For media segments the spec says the IV then defaults to
// the segment's media sequence number, which is what VHS's own fallback does;
// an init segment has no sequence number, and packagers write it with a zero
// IV. Verified empirically against a real encrypted stream: with a zero IV the
// init segment decrypts, the fMP4 parses, segments append and the video plays.
// A wrong IV would not "look a bit off" — the init segment would fail to parse
// and nothing would play at all, so playing IS the proof.
//
// HOW TO TELL WHEN THIS CAN GO. Delete this file and its call in
// PlaybackCore, then load an encrypted stream. If it plays, video.js has
// fixed it upstream. There is no other way for this to become a silent
// no-op — it either fires or it does not.

/** 16 zero bytes, as four zero words: the IV the init segment was encrypted
 * with. Rebuilt per message because the worker call transfers buffers. */
function zeroIv(): { bytes: ArrayBuffer; byteOffset: number; byteLength: number } {
  const bytes = new ArrayBuffer(16)
  return { bytes, byteOffset: 0, byteLength: 16 }
}

/**
 * True only for the exact message shape video.js's decrypter worker expects,
 * and only when the IV is the thing that is missing.
 *
 * Deliberately narrow. This wrapper sits on a global, so anything it does not
 * recognise with total confidence must pass through untouched — including
 * every worker message from the host page an embedded player is dropped into.
 */
function isDecryptMessageMissingIv(message: unknown): message is Record<string, unknown> {
  if (!message || typeof message !== 'object') return false
  const candidate = message as Record<string, unknown>
  if (candidate.iv !== undefined) return false
  if (typeof candidate.source !== 'string') return false

  const looksLikeBuffer = (value: unknown): boolean => {
    if (!value || typeof value !== 'object') return false
    const part = value as Record<string, unknown>
    return part.bytes instanceof ArrayBuffer && typeof part.byteLength === 'number'
  }

  return looksLikeBuffer(candidate.encrypted) && looksLikeBuffer(candidate.key)
}

let installed = false

/**
 * Installs the workaround. Idempotent, and safe to call before any player
 * exists. Called from PlaybackCore rather than at module load so it exists
 * only where a player does.
 */
export function installAesInitSegmentIvFix(): void {
  if (installed || typeof Worker === 'undefined') return
  installed = true

  const original = Worker.prototype.postMessage
  Worker.prototype.postMessage = function patchedPostMessage(
    this: Worker,
    message: unknown,
    ...rest: unknown[]
  ) {
    if (isDecryptMessageMissingIv(message)) {
      message.iv = zeroIv()
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (original as any).apply(this, [message, ...rest])
  } as typeof Worker.prototype.postMessage
}
